if (!customElements.get('product-customer-reviews')) {
  customElements.define('product-customer-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-review-i18n]')?.textContent || '{}');
      this.reviewLocale = this.dataset.reviewLocale || 'en-US';
      this.page = 1;
      this.perPage = Math.max(1, Math.min(12, Number(this.dataset.pageSize) || 12));
      this.rating = this.querySelector('[name="rating"]');
      this.sort = this.querySelector('[name="sort"]');
      this.grid = this.querySelector('.customer-reviews__grid');
      this.status = this.querySelector('[role="status"]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.retry = this.querySelector('.customer-reviews__retry');
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'customer-reviews__dialog';
      this.dialog.setAttribute('aria-labelledby', `${this.id}-dialog-title`);
      this.dialog.innerHTML = `<button type="button" class="customer-reviews__dialog-close" autofocus>Close review</button><div class="customer-reviews__stars" role="img"></div><h3 id="${this.id}-dialog-title"></h3><time></time><p class="customer-reviews__dialog-body"></p>`;
      this.append(this.dialog);
      this.dialog.querySelector('button').textContent = this.label('close', 'Close review');
      this.dialog.querySelector('button').addEventListener('click', () => this.dialog.close());
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-read-review]');
        if (button) this.openReview(button.dataset.readReview);
      });
      [this.rating, this.sort].forEach(control => control.addEventListener('change', () => { this.page = 1; this.render(); }));
      this.previous.addEventListener('click', () => this.changePage(-1));
      this.next.addEventListener('click', () => this.changePage(1));
      this.retry.addEventListener('click', () => this.load());
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '800px' });
      this.observer.observe(this);
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async load() {
      if (this.loading || this.reviews) return;
      this.loading = true;
      this.retry.hidden = true;
      try {
        const response = await fetch(this.dataset.source);
        if (!response.ok) throw new Error('Could not load reviews');
        const data = await response.json();
        if (data.productHandle !== (this.dataset.productHandle || '345-relief-cream') || data.reviews.length !== data.count) throw new Error('Invalid review data');
        this.reviews = data.reviews.map((review, index) => ({ ...review, timestamp: Date.parse(review.date), index }));
        [this.rating, this.sort].forEach(control => { control.disabled = false; });
        this.render();
      } catch {
        this.status.textContent = this.label('load_error', 'Showing the first [count] reviews. The remaining reviews could not be loaded. Please try again.', {count:this.perPage});
        this.retry.hidden = false;
      } finally { this.loading = false; }
    }

    changePage(change) {
      this.page += change;
      this.render();
      this.querySelector('.customer-reviews__controls').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      const focusTarget = this.next.disabled ? this.previous : this.next;
      focusTarget.focus({ preventScroll: true });
    }

    label(key, fallback, values = {}) {
      return (this.labels[key] || fallback).replace(/\[(\w+)\]/g, (match, name) => values[name] ?? match);
    }

    authorName(author) {
      return author === 'Anonymous' ? this.label('anonymous', 'Anonymous') : author || this.label('customer', 'Customer');
    }

    openReview(id) {
      const review = this.reviews?.find(review => review.id === id);
      if (!review) return;
      const stars = this.dialog.querySelector('.customer-reviews__stars');
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      stars.setAttribute('aria-label', this.label('of_five', '[rating] out of 5 stars', {rating:review.rating}));
      this.dialog.querySelector('h3').textContent = this.authorName(review.author);
      const date = this.dialog.querySelector('time');
      date.dateTime = review.date;
      date.textContent = new Intl.DateTimeFormat(this.reviewLocale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(review.date));
      this.dialog.querySelector('.customer-reviews__dialog-body').textContent = review.body || this.label('rating_only', 'Rating only');
      this.dialog.querySelector('.customer-reviews__dialog-photos')?.remove();
      if (this.dataset.showPhotos === 'true' && review.photos?.length) {
        const photos = document.createElement('div');
        photos.className = 'customer-reviews__dialog-photos';
        review.photos.forEach(url => photos.append(this.photo(url, review.author)));
        this.dialog.append(photos);
      }
      this.dialog.showModal();
    }

    render() {
      if (!this.reviews) return;
      const filtered = this.reviews.filter(review => this.rating.value === 'all' || review.rating === Number(this.rating.value));
      const sort = this.sort.value;
      filtered.sort((a, b) => {
        if (sort === 'highest') return b.rating - a.rating || b.timestamp - a.timestamp || a.index - b.index;
        if (sort === 'lowest') return a.rating - b.rating || b.timestamp - a.timestamp || a.index - b.index;
        return (sort === 'oldest' ? a.timestamp - b.timestamp : b.timestamp - a.timestamp) || a.index - b.index;
      });
      const pages = Math.max(1, Math.ceil(filtered.length / this.perPage));
      this.page = Math.max(1, Math.min(this.page, pages));
      const start = (this.page - 1) * this.perPage;
      this.grid.replaceChildren(...filtered.slice(start, start + this.perPage).map(review => this.card(review)));
      this.status.textContent = filtered.length ? this.label('status', 'Showing [start]–[end] of [count] reviews', {start:start+1,end:Math.min(start+this.perPage,filtered.length),count:filtered.length}) : this.label('empty', 'No reviews match these filters.');
      this.querySelector('[data-page]').textContent = this.label('pages', 'Page [page] of [pages]', {page:this.page,pages});
      this.previous.disabled = this.page === 1;
      this.next.disabled = this.page === pages;
    }

    card(review) {
      const card = document.createElement('article');
      card.className = 'customer-reviews__card';
      card.dataset.reviewId = review.id;
      const stars = document.createElement('div');
      stars.className = 'customer-reviews__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.label('of_five', '[rating] out of 5 stars', {rating:review.rating}));
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      const header = document.createElement('header');
      const author = document.createElement('h3');
      author.textContent = this.authorName(review.author);
      const date = document.createElement('time');
      date.dateTime = review.date.slice(0, 10);
      date.textContent = new Intl.DateTimeFormat(this.reviewLocale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(review.date));
      header.append(author, date);
      const body = document.createElement('p');
      body.className = 'customer-reviews__body' + (review.body ? '' : ' customer-reviews__body--empty');
      body.textContent = review.body || this.label('rating_only', 'Rating only');
      const read = document.createElement('button');
      read.type = 'button';
      read.className = 'customer-reviews__read';
      read.dataset.readReview = review.id;
      read.textContent = this.label('read', 'Read full review');
      read.setAttribute('aria-label', this.label('read_by', 'Read full review by [author]', {author:this.authorName(review.author)}));
      read.setAttribute('aria-haspopup', 'dialog');
      card.append(stars, header, body);
      if (this.dataset.showPhotos === 'true' && review.photos?.length) {
        const photos = document.createElement('div');
        photos.className = 'customer-reviews__photos';
        review.photos.forEach(url => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.readReview = review.id;
          button.setAttribute('aria-label', this.label('view_photo_by', 'View customer photo by [author]', {author:this.authorName(review.author)}));
          button.setAttribute('aria-haspopup', 'dialog');
          button.append(this.photo(url, review.author));
          photos.append(button);
        });
        card.append(photos);
      }
      card.append(read);
      return card;
    }

    photo(url, author) {
      const image = document.createElement('img');
      image.src = url;
      image.alt = this.label('photo_by', 'Customer photo by [author]', {author:this.authorName(author)});
      image.loading = 'lazy';
      return image;
    }
  });
}
